/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect } from 'react';
import Dropdown from 'rc-dropdown';
import Menu, { Item as MenuItem } from 'rc-menu';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import PropTypes from 'prop-types';
import 'rc-dropdown/assets/index.css';

import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { claimArrowKeys } from '../../utils/arrowKeyOwnership';

const DropdownIcon = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
`;

const DropdownContainerPrimary = styled('div')`
  display: inline-flex;
  align-items: center;
  ${(props) => props.visible && `
    cursor: pointer;
    padding: 2px 4px;
    margin: -2px -4px;
    border-radius: 4px;
    &:focus { outline: none; }
    &:focus-visible {
      outline: none;
      background: #F0F4FA;
      box-shadow: inset 0 0 0 2px #BFDBFE;
    }
  `}
`;

const MenuWrapper = styled('div')`
  display: flex;
  overflow: hidden;
  flex-direction: column;
`;

const CustomDivider = styled.div`
  height: 1px;
  margin: 8px 4px;
  background: #EEEEEE;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  border-radius: 4px;
  width: 100%;
  padding: 4px;
`;

const DeleteIconContainer = styled.div`
  display: flex;
  align-items: center;
  border-radius: 4px;
  padding: 4px;
`;

const ArrowDiv = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`;

const MenuText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 12pt;
  color: ${(props) => (props.selected ? '#9E9E9E' : '#444444')};
  margin-bottom: 0px;
  font-size: 16px;
`;

const SwapPropDropdownCircuit = ({
  size,
  primaryRowSwitch,
  setPrimaryRowSwitch,
  rowName,
  updateColumnName,
  name,
  unit,
  visible,
}) => {
  const menuConfig = [
    {
      condition: rowName === 'reps' || rowName === 'sets',
      items: [
        {
          label: 'Reps',
          selected: rowName === 'reps',
          action: () => updateColumnName(rowName, 'reps'),
        },
        {
          label: 'Sets',
          selected: rowName === 'sets',
          action: () => updateColumnName(rowName, 'sets'),
        },
      ],
    },
  ];

  // Get the format items based on the condition
  const formatItems = menuConfig
    .filter((config) => config.condition)
    .flatMap((config) => config.items);

  const isMainMenu = rowName === 'reps' || rowName === 'sets';

  const deleteAction = () => {
    const flippedStatus = !primaryRowSwitch[rowName];
    const newSwitch = { ...primaryRowSwitch };
    newSwitch[rowName] = flippedStatus;
    setPrimaryRowSwitch(newSwitch);
  };

  /** Controlled open + keyboard navigation (arrow/Enter/Escape). */
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);

  const navItems = isMainMenu
    ? [...formatItems, { action: deleteAction }]
    : [{ action: deleteAction }];

  useEffect(() => {
    if (open) setFocusedIndex(0);
  }, [open]);

  /** Own the arrow keys while open, so useCalendarKeyboardScroll leaves the
   * calendar where it is instead of sliding a row out from under the menu. */
  useEffect(() => {
    if (!open) return undefined;
    return claimArrowKeys();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex((p) => Math.min(p + 1, navItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((p) => Math.max(p - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = navItems[focusedIndex];
        if (item) { item.action(); setOpen(false); }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, focusedIndex, navItems]);

  const menu = (
    <Menu
      style={{
        width: 229,
        overflowY: 'auto',
        marginBottom: '0px',
        border: '1px solid #eeeeee',
        background: 'white',
      }}
    >
      <MenuWrapper style={{ padding: '8px' }}>
        {formatItems.map((item, index) => (
          <MenuItem
            key={`${item.label}-${index}`}
            style={{ cursor: 'pointer', background: focusedIndex === index ? '#F5F5F5' : 'white' }}
            /** Close on selection like the keyboard Enter path — with a
             * controlled `visible` rc-dropdown won't close itself. */
            onClick={() => { item.action(); setOpen(false); }}
          >
            <IconContainer>
              <ArrowDiv>
                <MenuText selected={item.selected}>{item.label}</MenuText>
                {item.selected && (
                  <IcomoonReact iconSet={iconSet} size={12} icon='checkmark' color='#9E9E9E' />
                )}
              </ArrowDiv>
            </IconContainer>
          </MenuItem>
        ))}
        {formatItems.length > 0 && <CustomDivider />}
        <MenuItem
          style={{ padding: 8, cursor: 'pointer', background: focusedIndex === navItems.length - 1 ? '#F5F5F5' : 'white' }}
          onClick={() => { deleteAction(); setOpen(false); }}
        >
          <DeleteIconContainer>
            <IcomoonReact
              iconSet={iconSet}
              size={17}
              icon='trashcan'
              color='#FF0505'
              style={{ marginRight: 5 }}
            />
            <ArrowDiv>
              <MenuText>Delete</MenuText>
            </ArrowDiv>
          </DeleteIconContainer>
        </MenuItem>
      </MenuWrapper>
    </Menu>
  );

  const deleteMenu = (
    <Menu
      style={{
        width: 229,
        overflowY: 'auto',
        marginBottom: '0px',
        border: '1px solid #eeeeee',
        background: 'white',
      }}
    >
      <MenuWrapper>
        <MenuItem
          style={{ padding: 8, cursor: 'pointer', background: focusedIndex === 0 ? '#F5F5F5' : 'white' }}
          onClick={() => { deleteAction(); setOpen(false); }}
        >
          <DeleteIconContainer>
            <IcomoonReact
              iconSet={iconSet}
              size={17}
              icon='trashcan'
              color='#FF0505'
              style={{ marginRight: 5 }}
            />
            <ArrowDiv>
              <MenuText>Delete</MenuText>
            </ArrowDiv>
          </DeleteIconContainer>
        </MenuItem>
      </MenuWrapper>
    </Menu>
  );

  return (
    <Dropdown
      overlay={isMainMenu ? menu : deleteMenu}
      aria-haspopup='true'
      trigger={visible ? ['click'] : []} /** Disable trigger if not visible */
      visible={open}
      onVisibleChange={setOpen}
    >
      <DropdownContainerPrimary
        onClick={(e) => e.stopPropagation()}
        visible={visible}
        name={name}
        {...(visible ? {
          role: 'button',
          tabIndex: 0,
          onKeyDown: (e) => {
            if (!open && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
              e.preventDefault();
              e.stopPropagation();
              setOpen(true);
            }
          },
        } : {})}
      >
        <DropdownIcon padding={size}>
          {name}
          {' '}
          {unit || null}
          {/* do not show the dropdown icon if the dropdown is disabled */}
          {!visible ? null : (
            <IcomoonReact
              iconSet={iconSet}
              size={7}
              icon='down-arrow'
              color='black'
            />
          )}
        </DropdownIcon>
      </DropdownContainerPrimary>
    </Dropdown>
  );
};

SwapPropDropdownCircuit.propTypes = {
  size: PropTypes.number.isRequired,
  primaryRowSwitch: PropTypes.instanceOf(Object).isRequired,
  setPrimaryRowSwitch: PropTypes.func.isRequired,
  rowName: PropTypes.string.isRequired,
  updateColumnName: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  unit: PropTypes.string.isRequired,
  visible: PropTypes.bool,
};

SwapPropDropdownCircuit.defaultProps = {
  visible: true,
};

export default SwapPropDropdownCircuit;
